import type { Debt } from '@/types'
import type { IDebtRepository } from './types'
import { SEED_DEBTS } from '@/lib/seedData'
import { isDebtsArray } from './validators'

const STORAGE_KEY = 'debtos:debts:v1'

export class LocalStorageRepo implements IDebtRepository {
  private read(): Debt[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) {
        // Persist seed data on first read so backups always capture real state.
        // Bootstrap-write failures are non-fatal — the seed array is fine to
        // return in-memory even if it never made it to disk this tick.
        try {
          this.write(SEED_DEBTS)
        } catch {
          /* non-fatal — see write() for the surfaced-error contract */
        }
        return SEED_DEBTS
      }
      const parsed: unknown = JSON.parse(raw)
      if (!isDebtsArray(parsed)) {
        // Malformed payload — most often from a restore that wrote an
        // unexpected shape into this key. Fall back to seed (empty in
        // prod) so consumers' `.filter()` / `.reduce()` calls don't
        // crash a tick later. See `repository/validators.ts` for why
        // we don't strict-validate every field.
        console.warn(
          `[debtos] Malformed payload at ${STORAGE_KEY}; resetting to seed. ` +
            'Most likely caused by a restore from an incompatible backup.'
        )
        try {
          this.write(SEED_DEBTS)
        } catch {
          /* non-fatal — see write() */
        }
        return SEED_DEBTS
      }
      return parsed
    } catch {
      return SEED_DEBTS
    }
  }

  /**
   * Throws on any `setItem` failure (QuotaExceededError under Safari private
   * mode, near-full storage on Android Chrome, large-ledger pushes) so the
   * write-path caller — store action → dialog handler — can surface the
   * problem to the user instead of leaving in-memory state silently ahead of
   * disk. Seed bootstrap in `read()` swallows this; user-triggered writes
   * propagate.
   */
  private write(debts: Debt[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(debts))
    } catch (err) {
      throw new Error(
        `Failed to save to local storage (${STORAGE_KEY}): ` +
          (err instanceof Error ? err.message : String(err))
      )
    }
  }

  async getAll(): Promise<Debt[]> {
    return this.read()
  }

  async getById(id: string): Promise<Debt | null> {
    return this.read().find((d) => d.id === id) ?? null
  }

  async upsert(debt: Debt): Promise<Debt> {
    const debts = this.read()
    const idx = debts.findIndex((d) => d.id === debt.id)
    const updated = { ...debt, updatedAt: new Date().toISOString() }
    if (idx >= 0) {
      debts[idx] = updated
    } else {
      debts.push(updated)
    }
    this.write(debts)
    return updated
  }

  /** v0.8.56 — one device, nothing to merge: store the new version. */
  async patch(_before: Debt, after: Debt): Promise<Debt> {
    return this.upsert(after)
  }

  async delete(id: string): Promise<void> {
    const debts = this.read().filter((d) => d.id !== id)
    this.write(debts)
  }

  async bulkReplace(debts: Debt[]): Promise<void> {
    this.write(debts)
  }

  async appendMany(debts: Debt[]): Promise<void> {
    // v0.8.44 — single read + single write so the persisted state is
    // atomic per browser-tick: a concurrent reader either sees the old
    // state or the appended state, never a partial.
    const existing = this.read()
    this.write([...existing, ...debts])
  }
}
